/**
 * Home-page A/B test: Clarity v2 ("/") vs Maison ("/lp/maison").
 *
 * The bucketing runs as an inline script at the top of index.html, before React
 * loads, so a Maison visitor never sees a flash of Clarity. That script repeats
 * `pickHomeVariant` by hand; src/test/experiment.test.ts keeps both in sync.
 */
export const EXPERIMENT_ID = "home_v2_vs_maison";
export const EXPERIMENT_COOKIE = "pf_exp_home";
export const EXPERIMENT_ON = true;
export const MAISON_SHARE = 0.5;
export const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

export type HomeVariant = "clarity" | "maison";

const isVariant = (value: unknown): value is HomeVariant => value === "clarity" || value === "maison";

/** Reads the bucket from a `document.cookie` string. */
export function readVariantCookie(cookie: string): HomeVariant | null {
  const match = new RegExp(`(?:^|;\\s*)${EXPERIMENT_COOKIE}=([^;]*)`).exec(cookie);
  return match && isVariant(match[1]) ? match[1] : null;
}

/**
 * Decides the bucket for a visit to "/".
 * Order: `?variant=` QA override, then the cookie, then a fresh 50/50 draw.
 * `persist` is true when the cookie must be (re)written.
 */
export function pickHomeVariant({
  search,
  cookie,
  random,
  share = MAISON_SHARE,
}: {
  search: string;
  cookie: string;
  random: () => number;
  share?: number;
}): { variant: HomeVariant; persist: boolean } {
  const forced = new URLSearchParams(search).get("variant");
  if (isVariant(forced)) return { variant: forced, persist: true };
  const stored = readVariantCookie(cookie);
  if (stored) return { variant: stored, persist: false };
  return { variant: random() < share ? "maison" : "clarity", persist: true };
}

/** The current visitor's bucket, or null when they were never bucketed (e.g. came straight to an ad link). */
export function currentVariant(): HomeVariant | null {
  if (typeof document === "undefined") return null;
  return readVariantCookie(document.cookie);
}
